/**
 * SaveAsTemplateDialog - Save current week's roster as a reusable template
 * 
 * P1 Feature: Template-Based Smart Rostering
 */
import React, { useState } from 'react';
import { useAuth } from '../lib/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Save, Loader2, FileText } from 'lucide-react';

export default function SaveAsTemplateDialog({
  open,
  onOpenChange,
  siteId,
  siteName,
  weekStart,
  shiftCount = 0,
  totalHours = 0,
  onSuccess = null
}) {
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    template_name: siteName ? `${siteName} Standard Week` : 'Standard Week',
    description: '',
    recurrence_type: 'weekly',
  });

  const handleSave = async () => {
    if (!formData.template_name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/roster/templates/save-from-week', {
        template_name: formData.template_name.trim(),
        site_id: siteId,
        week_start: weekStart,
        description: formData.description || null,
        recurrence_type: formData.recurrence_type,
      });

      toast.success(`Template "${response.data.template_name}" created with ${response.data.shift_count} shifts`);
      onOpenChange(false);
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create template';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" />
            Save as Template
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Save the current week's roster as a reusable template for {siteName || 'this site'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Template Name *</Label>
            <Input
              value={formData.template_name}
              onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
              placeholder="e.g., Buttonderry Standard Week"
              className="bg-[#09090b] border-[#3f3f46]"
              data-testid="template-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Description (optional)</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe this template..."
              className="bg-[#09090b] border-[#3f3f46] min-h-[80px]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Recurrence Type</Label>
            <Select
              value={formData.recurrence_type}
              onValueChange={(v) => setFormData({ ...formData, recurrence_type: v })}
            >
              <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                <SelectItem value="weekly">Weekly (repeat every week)</SelectItem>
                <SelectItem value="fortnightly">Fortnightly (every 2 weeks)</SelectItem>
                <SelectItem value="weekdays_only">Weekdays Only (Mon-Fri)</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Summary */}
          <div className="bg-[#09090b] p-3 rounded border border-[#3f3f46]">
            <p className="text-xs text-zinc-500 mb-1">Week: {weekStart}</p>
            <p className="text-sm text-white">
              {shiftCount > 0 ? (
                <>
                  {shiftCount} shift{shiftCount !== 1 ? 's' : ''} • {totalHours}h total
                </>
              ) : (
                <span className="text-amber-400">Will extract shifts from current week</span>
              )}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-zinc-600"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-emerald-500 hover:bg-emerald-600"
            disabled={loading || !formData.template_name.trim()}
            data-testid="save-template-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
